import {
  BASIC_COMPONENTS,
  BASIC_FUNCTIONS,
  Catalog,
  ResolvedBinding,
  isWritable,
  type A2uiMessage,
} from '@a2ui/web_core/v0_9'

/** Google's A2UI v0.9 basic catalog; the Vue renderer maps each component to Nuxt UI. */
export const A2UI_CATALOG = new Catalog(
  'https://a2ui.org/specification/v0_9/catalogs/basic/catalog.json',
  BASIC_COMPONENTS,
  BASIC_FUNCTIONS,
)

/** The UserAgent `renderUI` tool input: one surface's flat component list and data model. */
export interface A2uiSurfaceInput {
  components: Record<string, unknown>[]
  data?: Record<string, unknown>
}

export const toA2uiMessages = (
  surfaceId: string,
  { components, data }: A2uiSurfaceInput,
): A2uiMessage[] => [
  {
    version: 'v0.9',
    createSurface: { surfaceId, catalogId: A2UI_CATALOG.id },
  },
  { version: 'v0.9', updateComponents: { surfaceId, components } },
  ...(data
    ? [
        {
          version: 'v0.9' as const,
          updateDataModel: { surfaceId, value: data },
        },
      ]
    : []),
]

/** Current value of a resolved node prop; literals pass through. */
export const unwrap = <T = unknown>(prop: unknown): T | undefined =>
  (prop instanceof ResolvedBinding ? prop.value : prop) as T | undefined

export const writeBack = (prop: unknown, value: unknown): void => {
  if (prop instanceof ResolvedBinding && isWritable(prop)) prop.set(value)
}

/** A2UI basic-catalog icon names → Lucide. */
export const A2UI_ICONS: Record<string, string> = {
  accountCircle: 'i-lucide-circle-user',
  add: 'i-lucide-plus',
  arrowBack: 'i-lucide-arrow-left',
  arrowForward: 'i-lucide-arrow-right',
  attachFile: 'i-lucide-paperclip',
  calendarToday: 'i-lucide-calendar',
  call: 'i-lucide-phone-call',
  camera: 'i-lucide-camera',
  check: 'i-lucide-check',
  close: 'i-lucide-x',
  delete: 'i-lucide-trash-2',
  download: 'i-lucide-download',
  edit: 'i-lucide-pencil',
  event: 'i-lucide-calendar-days',
  error: 'i-lucide-circle-alert',
  fastForward: 'i-lucide-fast-forward',
  favorite: 'i-lucide-heart',
  favoriteOff: 'i-lucide-heart-off',
  folder: 'i-lucide-folder',
  help: 'i-lucide-circle-help',
  home: 'i-lucide-house',
  info: 'i-lucide-info',
  locationOn: 'i-lucide-map-pin',
  lock: 'i-lucide-lock',
  lockOpen: 'i-lucide-lock-open',
  mail: 'i-lucide-mail',
  menu: 'i-lucide-menu',
  moreVert: 'i-lucide-ellipsis-vertical',
  moreHoriz: 'i-lucide-ellipsis',
  notificationsOff: 'i-lucide-bell-off',
  notifications: 'i-lucide-bell',
  pause: 'i-lucide-pause',
  payment: 'i-lucide-credit-card',
  person: 'i-lucide-user',
  phone: 'i-lucide-phone',
  photo: 'i-lucide-image',
  play: 'i-lucide-play',
  print: 'i-lucide-printer',
  refresh: 'i-lucide-refresh-cw',
  rewind: 'i-lucide-rewind',
  search: 'i-lucide-search',
  send: 'i-lucide-send',
  settings: 'i-lucide-settings',
  share: 'i-lucide-share-2',
  shoppingCart: 'i-lucide-shopping-cart',
  skipNext: 'i-lucide-skip-forward',
  skipPrevious: 'i-lucide-skip-back',
  star: 'i-lucide-star',
  starHalf: 'i-lucide-star-half',
  starOff: 'i-lucide-star-off',
  stop: 'i-lucide-square',
  upload: 'i-lucide-upload',
  visibility: 'i-lucide-eye',
  visibilityOff: 'i-lucide-eye-off',
  volumeDown: 'i-lucide-volume-1',
  volumeMute: 'i-lucide-volume-x',
  volumeOff: 'i-lucide-volume-off',
  volumeUp: 'i-lucide-volume-2',
  warning: 'i-lucide-triangle-alert',
}
